import React, { useCallback, useState, useContext } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GlobalStateContext } from 'contexts/GlobalStateContext';
import { alertError, alertSuccess } from 'components/Alert';
import { CloudUploadRounded } from 'resource/muiImport';

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragOver' && prop !== 'hasValue',
})(({ theme, isDragOver, hasValue }) => ({
  width: hasValue ? '20px' : '100%',
  // border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isDragOver ? theme.palette.action.hover : 'transparent',
  transition: 'all 0.2s ease-in-out',
  minHeight: '35px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  }
}));

/**
 * fromDataObj: {
 *  refId: number, //Ex JobOrdId
 *  typId: number, //Ex JobOrdFileTypId
 * }
 */
/**
 * @param onFileSelect  optional. When given, the picked file is handed over INSTEAD of
 *                      being uploaded — for a record that does not exist yet, so there is
 *                      no id to attach to and the caller keeps the file until it saves.
 */
const FileDropCell = ({ formDataObj, uploadApiNm, onUploadSuccess, hasValue = false, disabled = false, onFileSelect }) => {
  const { ax } = useContext(GlobalStateContext);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = useCallback(async (file) => {
    if (!file || disabled) return;
    if (onFileSelect) {   // deferred: the caller uploads it once the record has an id
      onFileSelect(file);
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append("Label", file.name);
      for (const [key, value] of Object.entries(formDataObj)) {
        formData.append(key, value.toString());
      }
      const response = await ax.post(uploadApiNm, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        alertSuccess('File uploaded successfully');
        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }
      }
    } catch (error) {
      console.error('File upload error:', error);
      alertError('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, [ax, formDataObj, onUploadSuccess, disabled, uploadApiNm, onFileSelect]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload, disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    if (disabled) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    };
    input.click();
  }, [handleFileUpload, disabled]);

  if (disabled) {
    return (
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          Drop disabled
        </Typography>
      </Box>
    );
  }

  return (
    <DropZone
      isDragOver={isDragOver}
      hasValue={hasValue}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
    >
      {isUploading ? (
        <CircularProgress size={20} />
      ) : (
        <CloudUploadRounded color="action" fontSize="small" />
      )}
      {!isUploading && !hasValue && (
        <Typography variant="caption" sx={{ ml: 0.5 }}>
          Drop file
        </Typography>
      )}
    </DropZone>
  );
};

export default FileDropCell;
